export type FetcherResponse<T> = {
  data: T;
  status: number;
  statusText: string;
};

export type FetcherGetConfig = {
  responseType?: "arraybuffer" | "blob" | "json" | "text";
};

// ttlMs省略時はタブを閉じるまでキャッシュを保持する
export type FetcherGetCachedConfig = FetcherGetConfig & {
  ttlMs?: number;
};

type MemoryCacheEntry = {
  data: unknown;
  status: number;
  statusText: string;
  expiresAt: number | null; // nullならば、セッション中無制限にキャッシュを保持する
};

const memoryCache = new Map<string, MemoryCacheEntry>();
const inflight = new Map<string, Promise<FetcherResponse<unknown>>>();

function memoryCacheKey(url: string, responseType: string): string {
  return `${responseType}::${url}`;
}

export type FetcherPutConfig = {
  headers?: Record<string, string>;
};

async function parseBody<T>(
  res: Response,
  responseType: FetcherGetConfig["responseType"]
): Promise<T> {
  switch (responseType) {
    case "arraybuffer":
      return (await res.arrayBuffer()) as T;
    case "blob":
      return (await res.blob()) as T;
    case "text":
      return (await res.text()) as T;
    case "json":
    default:
      return (await res.json()) as T;
  }
}

function assertOk(res: Response): void {
  if (!res.ok) {
    throw new Error(`Request failed with status ${res.status}`);
  }
}

async function get<T>(
  url: string,
  config?: FetcherGetConfig
): Promise<FetcherResponse<T>> {
  const responseType = config?.responseType ?? "json";
  let res: Response;
  try {
    res = await fetch(url);
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    throw new Error(message);
  }
  assertOk(res);
  const data = (await parseBody<T>(res, responseType)) as T;
  return {
    data,
    status: res.status,
    statusText: res.statusText
  };
}

async function getCached<T>(
  url: string,
  config?: FetcherGetCachedConfig
): Promise<FetcherResponse<T>> {
  const responseType = config?.responseType ?? "json";
  const key = memoryCacheKey(url, responseType);

  const hit = memoryCache.get(key);
  if (hit) {
    if (hit.expiresAt === null || Date.now() < hit.expiresAt) {
      return {
        data: hit.data as T,
        status: hit.status,
        statusText: hit.statusText
      };
    }
    memoryCache.delete(key);
  }

  const pending = inflight.get(key);
  if (pending) {
    return pending as Promise<FetcherResponse<T>>;
  }

  const { ttlMs, ...getOnly } = config ?? {};
  const getConfig =
    Object.keys(getOnly).length > 0 ? (getOnly as FetcherGetConfig) : undefined;

  const p = get<T>(url, getConfig)
    .then((res) => {
      inflight.delete(key);

      const expiresAt: number | null =
        ttlMs === undefined ? null : Date.now() + ttlMs;

      memoryCache.set(key, {
        data: res.data,
        status: res.status,
        statusText: res.statusText,
        expiresAt
      });
      return res;
    })
    .catch((e) => {
      inflight.delete(key);
      throw e;
    });

  inflight.set(key, p);
  return p as Promise<FetcherResponse<T>>;
}

async function put<T>(
  url: string,
  body?: BodyInit | Buffer | null,
  config?: FetcherPutConfig
): Promise<FetcherResponse<T>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: "PUT",
      // Buffer は実行時は受け付けるが DOM の BodyInit に含まれない
      body: (body ?? null) as BodyInit | null,
      headers: config?.headers
    });
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    throw new Error(message);
  }
  assertOk(res);
  const buf = await res.arrayBuffer();
  const data = (buf.byteLength ? new Blob([buf]) : new Blob()) as T;
  return {
    data,
    status: res.status,
    statusText: res.statusText
  };
}

async function del<T>(url: string): Promise<FetcherResponse<T>> {
  let res: Response;
  try {
    res = await fetch(url, { method: "DELETE" });
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    throw new Error(message);
  }
  assertOk(res);
  const buf = await res.arrayBuffer();
  const data = (buf.byteLength ? new Blob([buf]) : new Blob()) as T;
  return {
    data,
    status: res.status,
    statusText: res.statusText
  };
}

const fetchClient = {
  get,
  getCached,
  put,
  delete: del
};

export default fetchClient;
